'use client';

import { useState, useRef, useCallback, KeyboardEvent, ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Send, Loader2 } from 'lucide-react';

const SOFT_LIMIT = 2000;
const HARD_LIMIT = 5000;

interface UnifiedMessageInputProps {
  onSend: (message: string) => void;
  onHelpQuestion: (question: string) => Promise<string>;
  disabled?: boolean;
  placeholder?: string;
}

export function UnifiedMessageInput({
  onSend,
  onHelpQuestion,
  disabled = false,
  placeholder = 'Typ je bericht...',
}: UnifiedMessageInputProps) {
  const [value, setValue] = useState('');
  const [isHelpMode, setIsHelpMode] = useState(false);
  const [isHelpLoading, setIsHelpLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    adjustHeight();
  };

  const charCount = value.length;
  const isOverSoftLimit = charCount > SOFT_LIMIT;
  const isOverHardLimit = charCount > HARD_LIMIT;

  const handleSend = useCallback(async () => {
    if (!value.trim() || disabled || isHelpLoading || value.length > HARD_LIMIT) return;

    const message = value.trim();
    setValue('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    if (isHelpMode) {
      // Send to help endpoint
      setIsHelpLoading(true);
      try {
        await onHelpQuestion(message);
      } finally {
        setIsHelpLoading(false);
        setIsHelpMode(false); // Reset help mode after sending
      }
    } else {
      // Send to main chat
      onSend(message);
    }
  }, [value, disabled, isHelpLoading, isHelpMode, onSend, onHelpQuestion]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleHelpMode = () => {
    if (!disabled && !isHelpLoading) {
      setIsHelpMode(!isHelpMode);
      textareaRef.current?.focus();
    }
  };

  const isLoading = disabled || isHelpLoading;
  const currentPlaceholder = isHelpMode
    ? 'Stel een verduidelijkende vraag...'
    : (isLoading ? 'Wachten op antwoord...' : placeholder);

  return (
    <div className="relative">
      {/* Help mode indicator - appears above input when active */}
      <AnimatePresence>
        {isHelpMode && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -top-8 left-0 right-0 flex items-center justify-center"
          >
            <span className="text-xs text-teal bg-teal/10 px-3 py-1 rounded-full border border-teal/20">
              Snelle Hulp Modus - Vraag over termen of concepten
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className={`
          flex items-end gap-2 p-2 rounded-2xl
          transition-all duration-300 ease-out
          ${isHelpMode
            ? 'bg-teal/5 border-2 border-teal/30 shadow-[0_0_20px_rgba(186,218,213,0.15)]'
            : 'bg-white border border-off-black/10 shadow-soft'
          }
        `}
        layout
      >
        {/* Help Toggle Button */}
        <motion.button
          onClick={toggleHelpMode}
          disabled={isLoading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`
            flex-shrink-0 p-2.5 rounded-xl
            transition-all duration-300
            focus:outline-none focus:ring-2 focus:ring-teal/30
            disabled:opacity-40 disabled:cursor-not-allowed
            ${isHelpMode
              ? 'bg-teal text-white shadow-md'
              : 'bg-off-black/5 text-off-black/50 hover:bg-teal/10 hover:text-teal'
            }
          `}
          aria-label={isHelpMode ? 'Hulpmodus afsluiten' : 'Hulpmodus starten'}
          title={isHelpMode ? 'Hulpmodus afsluiten (Esc)' : 'Stel een verduidelijkende vraag'}
        >
          <HelpCircle className="w-5 h-5" />
        </motion.button>

        {/* Textarea */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            placeholder={currentPlaceholder}
            rows={1}
            className={`
              w-full px-4 py-3 rounded-xl resize-none
              bg-transparent border-0
              text-off-black placeholder-off-black/40
              focus:outline-none
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
              ${isHelpMode ? 'placeholder-teal/60' : ''}
            `}
            style={{ minHeight: '48px', maxHeight: '150px' }}
          />
        </div>

        {/* Send Button */}
        <motion.button
          onClick={handleSend}
          disabled={isLoading || !value.trim() || isOverHardLimit}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`
            flex-shrink-0 p-3 rounded-xl
            transition-all duration-300
            focus:outline-none focus:ring-2 focus:ring-offset-2
            disabled:opacity-40 disabled:cursor-not-allowed
            ${isHelpMode
              ? 'bg-teal text-white hover:bg-teal-dark focus:ring-teal/50'
              : 'bg-orange-cta text-white hover:bg-orange-hover active:bg-orange-active focus:ring-orange-cta/50'
            }
          `}
          aria-label="Send message"
        >
          {isHelpLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </motion.button>
      </motion.div>

      {/* Character warning */}
      {isOverSoftLimit && (
        <div className={`mt-1.5 px-2 text-[11px] ${isOverHardLimit ? 'text-error-500' : 'text-amber-600'}`}>
          {isOverHardLimit
            ? `Te lang (${charCount.toLocaleString()}/${HARD_LIMIT.toLocaleString()}) — kort je bericht in om te versturen`
            : 'Tip: Korte antwoorden werken het beste'}
        </div>
      )}

      {/* Keyboard hint + character counter */}
      <div className="hidden sm:flex items-center justify-between mt-2 px-2">
        <span className="text-[10px] text-off-black/40">
          {isHelpMode ? 'Druk op Esc om hulpmodus af te sluiten' : 'Druk op ? voor snelle hulp'}
        </span>
        <div className="flex items-center gap-3">
          {isOverSoftLimit && (
            <span className={`text-[10px] ${isOverHardLimit ? 'text-error-500 font-medium' : 'text-amber-600'}`}>
              {charCount.toLocaleString()}/{HARD_LIMIT.toLocaleString()}
            </span>
          )}
          <span className="text-[10px] text-off-black/40">
            Enter om te versturen, Shift+Enter voor nieuwe regel
          </span>
        </div>
      </div>
    </div>
  );
}
